/**
 * React Query hooks for admin search endpoints.
 *
 * Provides debounced search across Wikidata and canonical entities
 * for use in the resolve freetext dialog.
 */

import { useQuery } from '@tanstack/react-query'
import { searchComposers, searchWorks } from '@/services/api/admin-search'

/**
 * Hook to search composers across Wikidata and canonical composers.
 * Only executes when query is at least 2 characters.
 */
export function useComposerSearch(query: string) {
  return useQuery({
    queryKey: ['admin-search', 'composers', query],
    queryFn: () => searchComposers(query),
    enabled: query.length >= 2,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to search works scoped to a specific composer.
 * Only executes when query is at least 2 characters and a composer is selected.
 */
export function useWorkSearch(
  query: string,
  composerId: string,
  composerType: 'wikidata' | 'canonical',
) {
  return useQuery({
    queryKey: ['admin-search', 'works', query, composerId, composerType],
    queryFn: () => searchWorks(query, composerId, composerType),
    enabled: query.length >= 2 && !!composerId,
    staleTime: 5 * 60 * 1000,
  })
}
